import { useEffect, useState, type ReactNode } from 'react'
import { Eye, Loader2, Save, UserMinus, UserPlus } from 'lucide-react'
import { DrawerSection } from '@/shared/ui/Drawer'
import { Field } from '@/shared/ui/Modal'
import { Note } from '@/shared/ui/states'
import { AsyncMultiSelect } from '@/shared/ui/filters'
import { explain } from '@/shared/api/problem'
import { cn } from '@/lib/utils'
import { listUnits } from '@/features/geography/api'
import { listMobilizers } from '@/features/mobilizers/api'
import {
  addHouseholdOccupant, addVoterChannel, createHousehold, listHouseholdOccupants,
  listHouseholds, listVoters, manageHouseholdMember, removeHouseholdOccupant,
  recordContact, recordConsent, recordPlacement, setMobilizer, setResidence, setSuppression,
  setSupport, setVoterChannelStatus, voterPii,
  CHANNEL_KIND_LABEL, CHANNEL_STATUS_LABEL, CONFIDENCE_LABEL, CONSENT_METHOD_LABEL,
  CONSENT_PURPOSE_LABEL, CONTACT_CHANNEL_LABEL, CONTACT_OUTCOME_LABEL, DIRECTION_LABEL,
  HOUSEHOLD_ROLE_LABEL, PLACEMENT_REASON_LABEL, RESIDENCE_SOURCE_LABEL,
  SENTIMENT_LABEL, SUPPORT_METHOD_LABEL, SUPPRESSION_LABEL,
} from './api'
import type { HouseholdOccupant } from './api'
import type { VoterDetailResponse, VoterPiiResponse } from '@/shared/api/types'

/**
 * The write controls for one voter, as panels rather than a dialog.
 *
 * They live inside the tab that shows the matching history, so recording a contact and
 * reading the contact log are the same screen. A separate "Record" dialog put every action
 * one place and every consequence another, which meant nobody could see what they had just
 * changed without closing something.
 *
 * Each panel owns its own busy state and its own error. One shared spinner across seven
 * forms would disable a form somebody is typing into because a different one is saving.
 */

/** `<select>` over a label map. The code is the value; the person reads the label. */
export function CodeSelect({ value, onChange, options, allowEmpty }: {
  value: string
  onChange: (v: string) => void
  options: Record<string, string>
  allowEmpty?: string
}) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
            className="erp-input w-full">
      {allowEmpty !== undefined && <option value="">{allowEmpty}</option>}
      {Object.entries(options).map(([code, label]) => (
        <option key={code} value={code}>{label}</option>
      ))}
    </select>
  )
}

/** Shared chrome: a titled panel, an error strip, and one Save that reports itself. */
function ControlPanel({ title, hint, onSave, disabled, children, saveLabel = 'Save' }: {
  title: string
  hint?: string
  onSave: () => Promise<unknown>
  disabled?: boolean
  children: ReactNode
  saveLabel?: string
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit() {
    setBusy(true); setError(null)
    try {
      await onSave()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <DrawerSection title={title}>
      {/* A container, not the viewport: the drawer is a third of the page, so `sm:` on a
          field row would lay out two or three columns for space this panel never has. */}
      <div className="@container px-4 py-3 space-y-3">
        {hint && <p className="text-erp-xs text-muted-foreground">{hint}</p>}
        {error && <Note tone="bad">{error}</Note>}
        {children}
        <div className="flex justify-end">
          <button type="button" className="erp-btn erp-btn-primary"
                  disabled={busy || disabled} onClick={() => { void submit() }}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Save className="h-3.5 w-3.5" />}
            {saveLabel}
          </button>
        </div>
      </div>
    </DrawerSection>
  )
}

type Props = { voter: VoterDetailResponse; onSaved: () => void }

export function SupportControl({ voter, onSaved }: Props) {
  const [score, setScore] = useState(voter.supportLevel ?? 50)
  const [method, setMethod] = useState('FIELD_REPORT')
  const [confidence, setConfidence] = useState('REPORTED')
  const [notes, setNotes] = useState('')

  useEffect(() => { setScore(voter.supportLevel ?? 50) }, [voter.id, voter.supportLevel])

  return (
    <ControlPanel
      title="Assess support"
      hint="Record this straight after the conversation. An assessment written from memory a week later is a different, weaker claim."
      onSave={async () => {
        await setSupport(voter.id, {
          supportLevel: score, methodCode: method, confidenceCode: confidence,
          notes: notes.trim() || null,
        })
        setNotes('')
        onSaved()
      }}
    >
      <Field label={`Score — ${score}`} required
             hint="0 is opposed, 100 is committed. The band is derived from this, so there is no second field to disagree with it.">
        <input type="range" min={0} max={100} step={1} value={score}
               onChange={(e) => setScore(Number(e.target.value))} className="w-full" />
      </Field>
      <div className="grid gap-3 @sm:grid-cols-2">
        <Field label="How did you learn this?" required>
          <CodeSelect value={method} onChange={setMethod} options={SUPPORT_METHOD_LABEL} />
        </Field>
        <Field label="How sure are you?" required
               hint="A doorstep conversation and a colleague's guess are not the same fact.">
          <CodeSelect value={confidence} onChange={setConfidence} options={CONFIDENCE_LABEL} />
        </Field>
      </div>
      <Field label="Notes">
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                  maxLength={4096} className="erp-input w-full" />
      </Field>
    </ControlPanel>
  )
}

export function ContactControl({ voter, onSaved }: Props) {
  const [channel, setChannel] = useState('DOOR_TO_DOOR')
  const [direction, setDirection] = useState('OUTBOUND')
  const [outcome, setOutcome] = useState('REACHED')
  const [sentiment, setSentiment] = useState('')
  const [notes, setNotes] = useState('')

  return (
    <ControlPanel
      title="Log a contact"
      hint="Recorded as happening now — this control is for logging a conversation as it happens."
      saveLabel="Log contact"
      onSave={async () => {
        await recordContact(voter.id, {
          channelCode: channel, directionCode: direction, outcomeCode: outcome,
          occurredAt: new Date().toISOString(),
          sentimentCode: sentiment || null, notes: notes.trim() || null,
        })
        setNotes(''); setSentiment('')
        onSaved()
      }}
    >
      <div className="grid gap-3 @sm:grid-cols-2 @lg:grid-cols-3">
        <Field label="Channel" required>
          <CodeSelect value={channel} onChange={setChannel} options={CONTACT_CHANNEL_LABEL} />
        </Field>
        <Field label="Direction" required>
          <CodeSelect value={direction} onChange={setDirection} options={DIRECTION_LABEL} />
        </Field>
        <Field label="Outcome" required
               hint="Wrong number and undeliverable are what justify a suppression later.">
          <CodeSelect value={outcome} onChange={setOutcome} options={CONTACT_OUTCOME_LABEL} />
        </Field>
      </div>
      <Field label="Sentiment">
        <CodeSelect value={sentiment} onChange={setSentiment} options={SENTIMENT_LABEL}
                    allowEmpty="Not recorded" />
      </Field>
      <Field label="Notes">
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                  maxLength={4096} className="erp-input w-full" />
      </Field>
    </ControlPanel>
  )
}

export function ConsentControl({ voter, onSaved }: Props) {
  const [purpose, setPurpose] = useState('CONTACT_SMS')
  const [granted, setGranted] = useState(true)
  const [method, setMethod] = useState('VERBAL_WITNESSED')
  const [witness, setWitness] = useState('')
  const [notes, setNotes] = useState('')

  return (
    <ControlPanel
      title="Record consent"
      hint="Withdrawal is a new entry, never a deletion — you have to be able to prove when contact stopped being permitted."
      onSave={async () => {
        await recordConsent(voter.id, {
          purposeCode: purpose, granted, methodCode: method,
          witnessName: witness.trim() || null,
          notes: notes.trim() || null,
        })
        setWitness('')
        setNotes('')
        onSaved()
      }}
    >
      <Field label="What did they agree to?" required
             hint="Thirteen purposes, because agreeing to a text message is not agreeing to a home visit.">
        <CodeSelect value={purpose} onChange={setPurpose} options={CONSENT_PURPOSE_LABEL} />
      </Field>
      <div className="grid gap-3 @sm:grid-cols-2">
        <Field label="Granted or withdrawn" required>
          <select value={granted ? 'y' : 'n'} className="erp-input w-full"
                  onChange={(e) => setGranted(e.target.value === 'y')}>
            <option value="y">Granted</option>
            <option value="n">Withdrawn</option>
          </select>
        </Field>
        <Field label="How was it captured?" required>
          <CodeSelect value={method} onChange={setMethod} options={CONSENT_METHOD_LABEL} />
        </Field>
      </div>
      <Field label="Witness name">
        <input value={witness} onChange={(e) => setWitness(e.target.value)} maxLength={120}
               className="erp-input w-full" />
      </Field>
      <Field label="Notes" hint="The part no code can carry: what they actually said, and who was in the room. It shows on the history below.">
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                  rows={2} maxLength={4096} className="erp-input w-full" />
      </Field>
    </ControlPanel>
  )
}

export function PlacementControl({ voter, onSaved }: Props) {
  const [station, setStation] = useState<{ value: string; label: string }[]>([])
  const [reason, setReason] = useState('DATA_CORRECTION')
  const [notes, setNotes] = useState('')

  return (
    <ControlPanel
      title="Correct the placement"
      hint="This fixes OUR record. It is not a transfer — a transfer changes the IEBC roll and goes through Voter Transfers with consent and review."
      disabled={station.length === 0}
      saveLabel="Record correction"
      onSave={async () => {
        await recordPlacement(voter.id, {
          toStationId: station[0]!.value, reasonCode: reason,
          notes: notes.trim() || null,
        })
        setStation([])
        setNotes('')
        onSaved()
      }}
    >
      <Field label="Correct station" required>
        <AsyncMultiSelect
          label="Station" emptyLabel="Search for a station"
          placeholder="Type a station name or code"
          selected={station}
          onChange={(next) => setStation(next.slice(-1))}
          search={async (term) => {
            const page = await listUnits({ q: term, type: 'POLLING_STATION' } as never, 0, 20)
            return page.content.map((u) => ({
              value: u.id, label: u.name || u.code || u.id, sub: u.code,
            }))
          }}
        />
      </Field>
      <Field label="Why is it changing?" required>
        <CodeSelect value={reason} onChange={setReason} options={PLACEMENT_REASON_LABEL} />
      </Field>
      <Field label="Notes" hint="Which station the canvasser found them at, and who said so. A reason code alone will not settle the same argument twice.">
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                  rows={2} maxLength={4096} className="erp-input w-full" />
      </Field>
    </ControlPanel>
  )
}

export function ResidenceControl({ voter, onSaved }: Props) {
  const [ward, setWard] = useState<{ value: string; label: string }[]>([])
  const [description, setDescription] = useState(voter.residenceDescription ?? '')
  const [source, setSource] = useState(voter.residenceSourceCode ?? 'SELF_DECLARED')

  return (
    <ControlPanel
      title="Record where they live"
      hint="Where somebody sleeps and where they are registered are different facts. This is the first one, and it is what a canvass round is planned against."
      onSave={async () => {
        await setResidence(voter.id, {
          wardId: ward[0]?.value ?? null,
          description: description.trim() || null,
          sourceCode: source,
        })
        onSaved()
      }}
    >
      <Field label="Ward they live in">
        <AsyncMultiSelect
          label="Ward" emptyLabel="Search for a ward"
          placeholder="Type a ward name"
          selected={ward}
          onChange={(next) => setWard(next.slice(-1))}
          search={async (term) => {
            const page = await listUnits({ q: term, type: 'WARD' } as never, 0, 20)
            return page.content.map((u) => ({
              value: u.id, label: u.name || u.code || u.id, sub: u.code,
            }))
          }}
        />
      </Field>
      <Field label="Address or landmark">
        <input value={description} onChange={(e) => setDescription(e.target.value)}
               maxLength={255} className="erp-input w-full" />
      </Field>
      <Field label="How do we know?" required>
        <CodeSelect value={source} onChange={setSource} options={RESIDENCE_SOURCE_LABEL} />
      </Field>
    </ControlPanel>
  )
}

export function MobilizerControl({ voter, onSaved }: Props) {
  const [mobilizer, setMobilizer_] = useState<{ value: string; label: string }[]>([])

  return (
    <ControlPanel
      title="Assign a mobilizer"
      hint="Who is answerable for this voter. Clearing it leaves them unassigned, which is a real state and shows on the register."
      saveLabel="Assign"
      onSave={async () => {
        await setMobilizer(voter.id, { mobilizerId: mobilizer[0]?.value ?? null })
        onSaved()
      }}
    >
      <Field label="Assigned mobilizer">
        <AsyncMultiSelect
          label="Mobilizer" emptyLabel="Search by name"
          placeholder="Type a mobilizer's name"
          selected={mobilizer}
          onChange={(next) => setMobilizer_(next.slice(-1))}
          search={async (term) => {
            const page = await listMobilizers({ q: term }, undefined, 20)
            return page.content.map((m) => ({
              value: m.id, label: m.fullName || m.id, sub: m.primaryUnitName ?? m.roleCode,
            }))
          }}
        />
      </Field>
    </ControlPanel>
  )
}

export function SuppressionControl({ voter, onSaved }: Props) {
  const [suppressed, setSuppressed] = useState(Boolean(voter.doNotContact))
  const [reason, setReason] = useState<string>(voter.doNotContactReasonCode ?? 'VOTER_REQUEST')

  useEffect(() => {
    setSuppressed(Boolean(voter.doNotContact))
    setReason(voter.doNotContactReasonCode ?? 'VOTER_REQUEST')
  }, [voter.id, voter.doNotContact, voter.doNotContactReasonCode])

  return (
    <ControlPanel
      title="Contactability"
      hint="Suppressed is not deleted. The record stays so that tomorrow's import cannot quietly resurrect somebody who asked to be left alone."
      onSave={async () => {
        await setSuppression(voter.id, {
          suppressed, reasonCode: suppressed ? reason : null,
        })
        onSaved()
      }}
    >
      <Field label="May we contact them?" required>
        <select value={suppressed ? 'n' : 'y'} className="erp-input w-full"
                onChange={(e) => setSuppressed(e.target.value === 'n')}>
          <option value="y">Yes — may be contacted</option>
          <option value="n">No — suppress</option>
        </select>
      </Field>
      {suppressed && (
        <Field label="Why?" required
               hint="At their request is the one with legal weight. Record it accurately.">
          <CodeSelect value={reason} onChange={setReason} options={SUPPRESSION_LABEL} />
        </Field>
      )}
    </ControlPanel>
  )
}

/**
 * Join, re-role or leave a household.
 *
 * A household is a door, and a door is the unit a canvass round is actually planned in —
 * four voters at one address are one visit, not four. The tab used to render the household
 * a voter was already in and offer no way to put them in one, which made the whole feature
 * unreachable from the register.
 *
 * Creating a household needs a polling station; the voter's own is the only sensible
 * default and is the one used, so the common case is a name and a Save.
 */
export function HouseholdControl({ voter, onSaved }: Props) {
  const [household, setHousehold] = useState<{ value: string; label: string }[]>([])
  const [role, setRole] = useState<string>(voter.householdRoleCode ?? 'HEAD')
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [address, setAddress] = useState('')
  const [notes, setNotes] = useState('')

  const inHousehold = Boolean(voter.householdId)
  // A household belongs to a polling station, so a voter with no placement has no station
  // to create one at. Joining an existing one is equally impossible — the search is scoped
  // to the station. Say so rather than offering a form that cannot succeed.
  const stationId = voter.pollingStationId

  // Already a member: correct this voter's role, leave — and, because a household is not
  // finished until everyone behind the door is named, build out the rest of it here.
  if (inHousehold) {
    return (
      <>
        <ControlPanel
          title="This voter's place in it"
          hint="Correct this voter's role, or take them out of the household. Removing a voter does not delete the household — a door that turned out to be empty is still a door."
          saveLabel="Save role"
          onSave={async () => {
            await manageHouseholdMember(voter.householdId as string, {
              voterId: voter.id, action: 'assign', roleCode: role,
            })
            onSaved()
          }}
        >
          <Field label="Role in the household" required>
            <CodeSelect value={role} onChange={setRole} options={HOUSEHOLD_ROLE_LABEL} />
          </Field>
          <LeaveHousehold voter={voter} onSaved={onSaved} />
        </ControlPanel>
        <HouseholdMembers
          householdId={voter.householdId as string}
          stationId={stationId}
          currentVoterId={voter.id}
          onChanged={onSaved}
        />
      </>
    )
  }

  if (!stationId) {
    return (
      <DrawerSection title="Household">
        <div className="px-4 py-3">
          <Note tone="warn">
            This voter has no polling station, and a household belongs to one. Correct the
            placement first.
          </Note>
        </div>
      </DrawerSection>
    )
  }

  return (
    <ControlPanel
      title={creating ? 'Start a household' : 'Add to a household'}
      hint={creating
        ? 'The household is created at this voter’s own polling station, and they join it as its first member.'
        : 'Search for the door they live behind. Nothing here is created until you save.'}
      saveLabel={creating ? 'Create and join' : 'Add to household'}
      disabled={!creating && household.length === 0}
      onSave={async () => {
        const joining = household[0]
        const id = creating
          ? (await createHousehold({
              pollingStationId: stationId,
              name: name.trim() || null,
              addressDescription: address.trim() || null,
              notes: notes.trim() || null,
            })).id
          : joining?.value
        if (!id) return
        await manageHouseholdMember(id, { voterId: voter.id, action: 'add', roleCode: role })
        onSaved()
      }}
    >
      <div className="flex gap-2">
        <button type="button" onClick={() => setCreating(false)}
                className={creating ? 'erp-btn' : 'erp-btn erp-btn-primary'}>
          Join an existing one
        </button>
        <button type="button" onClick={() => setCreating(true)}
                className={creating ? 'erp-btn erp-btn-primary' : 'erp-btn'}>
          Start a new one
        </button>
      </div>

      {creating ? (
        <>
          <Field label="Name it" hint="How a canvasser would recognise the door — “Wainaina, plot 14”.">
            <input value={name} onChange={(e) => setName(e.target.value)}
                   maxLength={160} className="erp-input w-full" />
          </Field>
          <Field label="Address or landmark">
            <input value={address} onChange={(e) => setAddress(e.target.value)}
                   maxLength={255} className="erp-input w-full" />
          </Field>
          <Field label="Notes">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                      rows={2} maxLength={4096} className="erp-input w-full" />
          </Field>
        </>
      ) : (
        /* The picker's dropdown renders in flow rather than in a portal, so without room
           reserved for it, it lands on top of Save and swallows the first click after a
           selection. */
        <div className="min-h-[13rem]">
          <Field label="Which household?" required>
          <AsyncMultiSelect
            label="Household" emptyLabel="Search at this station"
            placeholder="Type a household name or code"
            selected={household}
            onChange={(next) => setHousehold(next.slice(-1))}
            search={async (term) => {
              // Scoped to the voter's own station: a household at a different station is
              // not a door this person can live behind.
              const page = await listHouseholds({ stationId })
              const q = term.trim().toLowerCase()
              return page.content
                .filter((h) => !q
                  || (h.name ?? '').toLowerCase().includes(q)
                  || h.code.toLowerCase().includes(q))
                .slice(0, 20)
                .map((h) => ({
                  value: h.id,
                  label: h.name || h.code,
                  sub: `${h.code} · ${h.memberCount} member${h.memberCount === 1 ? '' : 's'}`,
                }))
            }}
            />
          </Field>
        </div>
      )}

      <Field label="Role in the household" required>
        <CodeSelect value={role} onChange={setRole} options={HOUSEHOLD_ROLE_LABEL} />
      </Field>
    </ControlPanel>
  )
}

/** The remove half of household membership — destructive enough to sit on its own button. */
function LeaveHousehold({ voter, onSaved }: Props) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="pt-1">
      {error && <Note tone="bad">{error}</Note>}
      <button
        type="button"
        className="erp-btn text-red-700 dark:text-red-400"
        disabled={busy}
        onClick={() => {
          void (async () => {
            setBusy(true); setError(null)
            try {
              await manageHouseholdMember(voter.householdId as string, {
                voterId: voter.id, action: 'remove',
              })
              onSaved()
            } catch (err) {
              setError(explain(err))
            } finally {
              setBusy(false)
            }
          })()
        }}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserMinus className="h-3.5 w-3.5" />}
        Remove from household
      </button>
    </div>
  )
}

/**
 * The numbers and addresses a contact actually travels down.
 *
 * This is the control that clears **NO_ACTIVE_CHANNEL**, and the distinction it rests on is
 * the one the refusal never explained: consent says we are *permitted* to phone somebody,
 * a channel is the number we would phone. Recording consent for SMS on a voter with no
 * stored phone number still gets the contact refused, and until this existed nothing on the
 * record could clear it — channels could only be set at creation, so every imported voter
 * was permanently unreachable on every channel that resolves to a stored value.
 *
 * Values are PII: they are fetched from the audited endpoint, behind `voter:read-pii`, and
 * are not kept when the drawer closes.
 */
export function ChannelsControl({ voter, canPii, onSaved }: Props & { canPii: boolean }) {
  const [rows, setRows] = useState<VoterPiiResponse['channels'] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [kind, setKind] = useState('PHONE')
  const [value, setValue] = useState('')

  const load = async () => {
    setLoading(true); setError(null)
    try {
      setRows((await voterPii(voter.id)).channels)
    } catch (err) {
      setError(explain(err))
    } finally {
      setLoading(false)
    }
  }

  if (!canPii) {
    return (
      <DrawerSection title="Contact channels">
        <div className="px-4 py-3 text-erp-xs text-muted-foreground">
          Numbers and addresses require <code className="font-mono">voter:read-pii</code>.
        </div>
      </DrawerSection>
    )
  }

  return (
    <DrawerSection title="Contact channels">
      <div className="px-4 py-3 space-y-3">
        <p className="text-erp-xs text-muted-foreground">
          A contact by phone, SMS, WhatsApp or email is refused until the voter has an{' '}
          <b>active</b> channel of that kind — consent alone is not enough. Reading these
          writes an audit entry naming you and this voter.
        </p>
        {error && <Note tone="bad">{error}</Note>}

        {rows === null ? (
          <button type="button" className="erp-btn" disabled={loading}
                  onClick={() => { void load() }}>
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
            Show channels and record access
          </button>
        ) : (
          <>
            {rows.length === 0 && (
              <Note tone="warn">
                No channel is stored. Every phone, SMS, WhatsApp and email contact on this
                voter will be refused <span className="font-mono">NO_ACTIVE_CHANNEL</span>
                {' '}until one is added below.
              </Note>
            )}
            {rows.map((c) => (
              <ChannelRow key={c.id} voterId={voter.id} channel={c}
                          onChanged={() => { void load(); onSaved() }} />
            ))}
          </>
        )}

        <div className="grid grid-cols-[minmax(0,9rem)_1fr] gap-2 items-end">
          <Field label="Kind" required>
            <CodeSelect value={kind} onChange={setKind} options={CHANNEL_KIND_LABEL} />
          </Field>
          <Field label={kind === 'EMAIL' ? 'Email address' : 'Number'} required>
            <input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              inputMode={kind === 'EMAIL' ? 'email' : 'tel'}
              placeholder={kind === 'EMAIL' ? 'name@example.com' : '+254 7xx xxx xxx'}
              maxLength={160}
              className="erp-input w-full"
            />
          </Field>
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            className="erp-btn erp-btn-primary"
            disabled={loading || value.trim() === ''}
            onClick={() => {
              void (async () => {
                setLoading(true); setError(null)
                try {
                  await addVoterChannel(voter.id, {
                    kindCode: kind, value: value.trim(), primary: rows?.length === 0, verified: false,
                  })
                  setValue('')
                  await load()
                  onSaved()
                } catch (err) {
                  setError(explain(err))
                } finally {
                  setLoading(false)
                }
              })()
            }}
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Add channel
          </button>
        </div>
      </div>
    </DrawerSection>
  )
}

/** One stored channel, with the only two things you can do to it. */
function ChannelRow({ voterId, channel, onChanged }: {
  voterId: string
  channel: VoterPiiResponse['channels'][number]
  onChanged: () => void
}) {
  const [busy, setBusy] = useState(false)
  const active = channel.statusCode === 'ACTIVE'

  const set = (body: { statusCode?: string; primary?: boolean }) => {
    void (async () => {
      setBusy(true)
      try {
        await setVoterChannelStatus(voterId, channel.id, body)
        onChanged()
      } finally {
        setBusy(false)
      }
    })()
  }

  return (
    <div className="flex items-center gap-2 flex-wrap rounded-md border border-border px-2.5 py-2">
      <span className="text-erp-xs text-muted-foreground shrink-0">
        {CHANNEL_KIND_LABEL[channel.kindCode] ?? channel.kindCode}
      </span>
      <span className="font-mono text-erp-xs min-w-0 flex-1 break-all">{channel.value}</span>
      {channel.primary && <span className="text-erp-xs text-muted-foreground">primary</span>}
      <span className={cn('text-erp-xs',
        active ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400')}>
        {CHANNEL_STATUS_LABEL[channel.statusCode] ?? channel.statusCode}
      </span>
      {/* Withdrawn, never deleted: a number that stopped working must stay on the record,
          or tomorrow's import puts it straight back with no memory of why. */}
      <button type="button" className="erp-btn shrink-0" disabled={busy}
              onClick={() => set({ statusCode: active ? 'WITHDRAWN' : 'ACTIVE' })}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
        {active ? 'Retire' : 'Reinstate'}
      </button>
    </div>
  )
}

/**
 * Everyone at this door — and most of them may not be voters.
 *
 * This is the correction that matters most about households: membership used to be a column
 * on the voter, so a household could only ever contain people on the register. Real
 * households contain children, non-citizens and adults nobody has registered, and a canvass
 * plan that cannot see them is planning the wrong visit — "three voters here" and "three
 * voters and four children here" are different doors.
 *
 * So there are two ways to name somebody: pick them off the register, or just write their
 * name. A non-voter occupant is a name, a relationship and an optional birth year. There is
 * no phone, no identifier and no consent field, because there is nothing here that should
 * be used to contact a child — and because putting them on the register to make the
 * household complete would corrupt every count the platform produces.
 */
function HouseholdMembers({ householdId, stationId, currentVoterId, onChanged }: {
  householdId: string
  stationId?: string | null
  currentVoterId: string
  onChanged: () => void
}) {
  const [people, setPeople] = useState<HouseholdOccupant[] | null>(null)
  const [onRegister, setOnRegister] = useState(true)
  const [picked, setPicked] = useState<{ value: string; label: string }[]>([])
  const [name, setName] = useState('')
  const [birthYear, setBirthYear] = useState('')
  const [role, setRole] = useState<string>('CHILD')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = async () => {
    setError(null)
    try {
      setPeople(await listHouseholdOccupants(householdId))
    } catch (err) {
      setError(explain(err))
    }
  }

  useEffect(() => { void load() // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [householdId])

  const run = (fn: () => Promise<unknown>) => {
    void (async () => {
      setBusy(true); setError(null)
      try {
        await fn()
        setPicked([]); setName(''); setBirthYear('')
        await load()
        onChanged()
      } catch (err) {
        setError(explain(err))
      } finally {
        setBusy(false)
      }
    })()
  }

  const voters = people?.filter((p) => p.registeredVoter) ?? []
  const others = people?.filter((p) => !p.registeredVoter) ?? []

  return (
    <DrawerSection title="Who lives here">
      <div className="@container px-4 py-3 space-y-3">
        <p className="text-erp-xs text-muted-foreground">
          Name everyone behind this door, not only the voters. A canvasser knocks once and
          meets all of them, and how many people live here is what sizes the visit.
        </p>
        {error && <Note tone="bad">{error}</Note>}

        {people === null && (
          <div className="text-erp-xs text-muted-foreground inline-flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading the household…
          </div>
        )}

        {people !== null && (
          <p className="text-erp-xs text-muted-foreground">
            {people.length} at this door · {voters.length} on the register ·{' '}
            {others.length} not
          </p>
        )}

        {people?.map((p) => (
          <div key={p.id}
               className="flex items-center gap-2 flex-wrap rounded-md border border-border px-2.5 py-2">
            <span className="min-w-0 flex-1 text-erp-sm truncate">{p.fullName}</span>
            <span className="text-erp-xs text-muted-foreground shrink-0">
              {HOUSEHOLD_ROLE_LABEL[p.roleCode] ?? p.roleCode}
              {p.birthYear ? ` · b. ${p.birthYear}` : ''}
            </span>
            {/* Said plainly, because it changes what you may do with the row: an occupant
                who is not on the register has no support level, no consent and nothing to
                contact them on, and none of that is an omission. */}
            <span className={cn('text-erp-xs shrink-0',
              p.registeredVoter ? 'text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground')}>
              {p.registeredVoter ? 'voter' : 'not on the register'}
            </span>
            {p.voterId === currentVoterId
              ? <span className="text-erp-xs text-muted-foreground shrink-0">this voter</span>
              : (
                <button type="button" className="erp-btn shrink-0" disabled={busy}
                        onClick={() => run(() => removeHouseholdOccupant(householdId, p.id))}>
                  <UserMinus className="h-3.5 w-3.5" /> Remove
                </button>
              )}
          </div>
        ))}

        {people?.length === 0 && (
          <Note tone="warn">
            Nobody is listed. The household exists but names nobody, which is the one state
            it cannot be useful in.
          </Note>
        )}

        <div className="pt-1 flex gap-2">
          <button type="button" onClick={() => setOnRegister(true)}
                  className={onRegister ? 'erp-btn erp-btn-primary' : 'erp-btn'}>
            Add a voter
          </button>
          <button type="button" onClick={() => setOnRegister(false)}
                  className={onRegister ? 'erp-btn' : 'erp-btn erp-btn-primary'}>
            Add someone not on the register
          </button>
        </div>

        {onRegister ? (
          /* Reserved room: the picker's dropdown renders in flow, not in a portal. */
          <div className="min-h-[13rem] space-y-3">
            <Field label="Which voter?" required
                   hint="Searches the register at this polling station.">
              <AsyncMultiSelect
                label="Voter" emptyLabel="Search by name"
                placeholder="Type a name"
                selected={picked}
                onChange={(next) => setPicked(next.slice(-1))}
                search={async (term) => {
                  const page = await listVoters(
                    { q: term, ...(stationId ? { stationId } : {}) }, undefined, 20)
                  return page.content
                    .filter((v) => v.householdId !== householdId)
                    .map((v) => ({
                      value: v.id,
                      label: v.displayName,
                      // Say up front who is already spoken for — the backend refuses them,
                      // and finding that out after a click is a worse way to learn it.
                      sub: v.householdId
                        ? 'already in another household'
                        : v.pollingStationName ?? undefined,
                    }))
                }}
              />
            </Field>
            <RoleAndAdd
              role={role} setRole={setRole} busy={busy}
              disabled={picked.length === 0}
              onAdd={() => picked[0] && run(() => addHouseholdOccupant(householdId, {
                voterId: picked[0]!.value, roleCode: role,
              }))}
            />
          </div>
        ) : (
          <div className="space-y-3">
            <Note tone="info">
              A child, a non-citizen, or an adult nobody has registered. They are recorded
              here and nowhere else — not on the register, not in any count, and with
              nothing to contact them on.
            </Note>
            <div className="grid gap-3 @sm:grid-cols-2">
              <Field label="Their name" required>
                <input value={name} onChange={(e) => setName(e.target.value)}
                       maxLength={160} className="erp-input w-full" />
              </Field>
              <Field label="Year of birth"
                     hint="Optional. A year, not a date — enough to tell a child from an adult.">
                <input value={birthYear} onChange={(e) => setBirthYear(e.target.value)}
                       inputMode="numeric" maxLength={4} placeholder="2014"
                       className="erp-input w-full" />
              </Field>
            </div>
            <RoleAndAdd
              role={role} setRole={setRole} busy={busy}
              disabled={name.trim() === ''}
              onAdd={() => run(() => addHouseholdOccupant(householdId, {
                fullName: name.trim(),
                roleCode: role,
                birthYear: /^\d{4}$/.test(birthYear.trim()) ? Number(birthYear.trim()) : null,
              }))}
            />
          </div>
        )}
      </div>
    </DrawerSection>
  )
}

/** The role select and the Add button, identical on both halves of the form. */
function RoleAndAdd({ role, setRole, busy, disabled, onAdd }: {
  role: string
  setRole: (v: string) => void
  busy: boolean
  disabled: boolean
  onAdd: () => void
}) {
  return (
    <div className="grid gap-3 @sm:grid-cols-2 items-end">
      <Field label="Their role" required>
        <CodeSelect value={role} onChange={setRole} options={HOUSEHOLD_ROLE_LABEL} />
      </Field>
      <div className="flex justify-end">
        <button type="button" className="erp-btn erp-btn-primary"
                disabled={busy || disabled} onClick={onAdd}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <UserPlus className="h-3.5 w-3.5" />}
          Add to household
        </button>
      </div>
    </div>
  )
}
